import {
  UserCircle,
  Briefcase,
  Award,
  FolderGit2,
  GraduationCap,
  FileText,
  Trophy,
  FileSignature,
  Building2,
  CheckCircle2,
  PanelLeftClose,
  PanelRightClose,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type EditorTabType =
  | "Personal Information"
  | "Professional Information"
  | "Experience"
  | "Skills"
  | "Projects"
  | "Education"
  | "Certifications"
  | "Awards"
  | "Summary";

export const MENU_ITEMS: {
  icon: LucideIcon;
  label: EditorTabType;
  status?: "done";
}[] = [
  { icon: UserCircle, label: "Personal Information" },
  { icon: Briefcase, label: "Professional Information" },
  { icon: Building2, label: "Experience" },
  { icon: Award, label: "Skills" },
  { icon: FolderGit2, label: "Projects" },
  { icon: GraduationCap, label: "Education" },
  { icon: FileText, label: "Certifications" },
  { icon: Trophy, label: "Awards" },
  { icon: FileSignature, label: "Summary" },
];

export function EditorSidebar({
  activeTab,
  setActiveTab,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}: {
  activeTab: EditorTabType;
  setActiveTab: (tab: EditorTabType) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 shrink-0 transition-all duration-300 h-full",
        isSidebarCollapsed ? "w-[80px]" : "w-[280px]",
      )}
    >
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm flex flex-col h-full relative">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          {!isSidebarCollapsed && (
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Profile Sections
            </h3>
          )}
          {isSidebarCollapsed && (
            <div className="w-full flex justify-center">
              <h3 className="text-xs font-bold text-slate-500">Nav</h3>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {MENU_ITEMS.map((item, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(item.label)}
              className={cn(
                "w-full flex items-center p-3 rounded-lg text-sm cursor-pointer transition-colors group relative",
                activeTab === item.label
                  ? "bg-primary/10 text-primary font-bold"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800",
                isSidebarCollapsed ? "justify-center" : "justify-between",
              )}
              title={isSidebarCollapsed ? item.label : undefined}
            >
              <span className="flex items-center gap-3">
                <item.icon
                  className={cn("h-4 w-4", isSidebarCollapsed && "h-5 w-5")}
                />
                {!isSidebarCollapsed && item.label}
              </span>
              {!isSidebarCollapsed && item.status === "done" && (
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              )}
            </button>
          ))}
        </div>

        <div className="p-3 border-t border-slate-200 dark:border-slate-800 flex justify-center">
          <Button
            variant="ghost"
            className={cn(
              "text-slate-500 h-8 text-xs",
              !isSidebarCollapsed && "w-full",
            )}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            {isSidebarCollapsed ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4 mr-2" /> Collapse Sections
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
